import locale from "localization";
import Button from "../button/button";
import { useEffect, useRef, useState } from "react";
import styles from "./select-file.module.scss";
import Image from "../image/image";
import Text from "../text/text";
import { isBoolean, isString } from "lodash";
import { StocqIcon } from "images";
import { Warning } from "@material-ui/icons";
import Dropzone from "react-dropzone";

export const extractDirectoryWithFile = (url) => {
  const match = url.match(/.+\/([^/]+\.[a-z]+)(?=\/|$)/i);
  return match ? match[1] : null;
};

export const extractFileSizeFromUrl = (url) => {
  if (!isString(url)) return "0.00 KB";
  const match = url.match(/_([0-9]+(?:\.[0-9]+)?)(KB|MB)\./);
  return match ? match[1] + match[2] : "0.00 KB";
};

const SelectMultipleFiles = (props) => {
  const refFile = useRef(null);
  // eslint-disable-next-line
  const [isPreview, setIsPreview] = useState(false);
  const [fileData, setFileData] = useState([]);
  const [error, setError] = useState();
  const [urlValue, setUrlValue] = useState(null);

  const validatedDimension = isBoolean(props?.validatedDimension)
    ? props?.validatedDimension
    : true;

  useEffect(() => {
    if (props?.value instanceof File) {
      const preview = URL.createObjectURL(props?.value);
      setFileData([
        {
          name: props?.value.name,
          size: `${(props?.value.size / 1024).toFixed(2)} KB`,
          preview,
          raw: props?.value,
        },
      ]);
      setUrlValue(preview);
    }

    if (isString(props?.value)) {
      setUrlValue(props?.value);
      setFileData([]);
    }

    if (!props?.value) {
      setUrlValue("");
      setFileData([]);
    }

    if (refFile?.current) {
      refFile.current.value = "";
    }
  }, [props?.value]);

  const accept = props?.type === "image" ? "image/*" : undefined;

  const handleFileChange = (event) => {
    setError(null);
    const selectedFiles = Array.isArray(event) ? event : Array.from(event.target.files || []);

    const maxFiles = props?.maxFiles || Infinity;
    const filesToProcess = selectedFiles.slice(0, maxFiles);
    const validFileTypes = props?.type === "image" ? ["image/jpeg", "image/jpg", "image/png"] : [];

    const processedFiles = [];

    let processedCount = 0;
    let hasError = false;

    filesToProcess.forEach((file) => {
      const fileSizeKb = file.size / 1024;

      if (validFileTypes.length && !validFileTypes.includes(file.type)) {
        setError(props?.errorFileFormatMessage || locale.invalidFileType);
        hasError = true;
        return;
      }

      if (fileSizeKb > (props?.maxSizeKb || Infinity)) {
        setError(props?.errorFileSizeMessage || `${locale.maxLimitToUploadReached}`);
        hasError = true;
        return;
      }

      const preview = URL.createObjectURL(file);

      if (props?.type === "image") {
        const reader = new FileReader();
        reader.onload = (e) => {
          const img = new window.Image();
          img.onload = () => {
            if (validatedDimension && (img.width > 100 || img.height > 100)) {
              setError(locale.invalidDimensions);
              hasError = true;
              return;
            }

            processedFiles.push({
              name: file.name,
              size: `${fileSizeKb.toFixed(2)} KB`,
              preview,
              raw: file,
            });

            processedCount++;
            if (processedCount === filesToProcess.length && !hasError) {
              setFileData(processedFiles);
              props?.onChange?.(
                props?.name,
                processedFiles.map((f) => f.raw)
              );
            }
          };
          img.onerror = () => {
            setError(locale.invalidImage);
            hasError = true;
          };
          img.src = e.target.result;
        };
        reader.readAsDataURL(file);
      } else {
        processedFiles.push({
          name: file.name,
          size: `${fileSizeKb.toFixed(2)} KB`,
          preview,
          raw: file,
        });
        processedCount++;
      }
    });

    if (props?.type !== "image" && processedFiles.length && !hasError) {
      setFileData(processedFiles);
      props?.onChange?.(
        props?.name,
        processedFiles.map((f) => f.raw)
      );
    }
  };

  const renderResult = () => {
    if (error) return;
    if (props?.renderResult) {
      return props?.renderResult();
    }

    if (!Array.isArray(fileData)) return null;

    return fileData.map((file, index) => (
      <div key={index} className={styles.result}>
        <Image onClick={() => setIsPreview(true)} src={file.preview} alt={file.name} />
        <div>
          <Text className={styles.fileName}>{file.name}</Text>
          <Text className={styles.fileSize}>{file.size}</Text>
        </div>
      </div>
    ));
  };

  return (
    <div className={`${styles.content}`}>
      {!props?.isView && (
        <Dropzone onDrop={handleFileChange} multiple={props?.multiple}>
          {({ getRootProps, getInputProps, isDragActive }) => (
            <div
              {...getRootProps()}
              className={`${styles.dropzone} ${isDragActive ? styles.dragActive : ""}`}
            >
              <input
                {...getInputProps()}
                type="file"
                hidden
                ref={refFile}
                onChange={handleFileChange}
                accept={accept}
                multiple={props?.multiple}
              />
              <Button
                onClick={() => {
                  if (error) setError(null);
                  props?.onClick?.();
                  refFile?.current?.click();
                }}
                primary
                style={{
                  backgroundColor: "#eae8ff",
                  color: "#5349b7",
                  fontWeight: "500",
                  ...props?.buttonProps?.style,
                }}
                {...props?.buttonProps}
              >
                {fileData.length > 0 || urlValue
                  ? locale.replace
                  : props?.text || locale.browseYourFiles}
              </Button>
              {error && (
                <Text
                  subtitle
                  color={"red"}
                  style={{
                    marginTop: "5px",
                    width: "160px",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                    ...props?.errorStyle,
                  }}
                >
                  <Warning fontSize="small" style={{ fontSize: "12px" }} /> <span>{error}</span>
                </Text>
              )}
            </div>
          )}
        </Dropzone>
      )}
      {fileData.length > 0 || urlValue
        ? renderResult()
        : !props?.renderResult && (
            <div className={styles.result}>
              <Image src={StocqIcon} alt="No file selected" />
              <Text>{locale.noFileSelected}</Text>
            </div>
          )}
    </div>
  );
};

export default SelectMultipleFiles;
